'use client';
import { resolveHowItWorksIcon } from '@/lib/howItWorksIcons';

const DEFAULT_STEPS = [
  {
    icon: 'phone',
    title: 'Консультація',
    desc: "Зв'яжіться з нами — безкоштовна консультація щодо вашого проєкту. Обговорюємо бажання, терміни та бюджет.",
    detail:
      "Наш менеджер відповідає протягом 1 години. Ми виїжджаємо на об'єкт для оцінки, прослуховуємо всі ваші побажання та пропонуємо оптимальне рішення.",
  },
  {
    icon: 'file-text',
    title: 'Кошторис та договір',
    desc: 'Складаємо детальний кошторис та підписуємо договір з чітко прописаними термінами та гарантіями.',
    detail:
      "Кошторис фіксований — ніяких прихованих платежів у процесі. У договорі прописані всі матеріали, терміни та гарантійні зобов'язання.",
  },
  {
    icon: 'hammer',
    title: 'Виконання робіт',
    desc: 'Розпочинаємо будівництво. Ви отримуєте регулярні звіти про хід виконання робіт на кожному етапі.',
    detail:
      'Щотижневі фото- та відеозвіти з майданчика. Ви завжди знаєте, що відбувається. Виїзд на перевірку — у будь-який момент.',
  },
  {
    icon: 'check-circle',
    title: "Здача об'єкту",
    desc: "Приймання готового об'єкту, оформлення гарантійних документів та подальша підтримка.",
    detail:
      'Фінальний огляд разом із замовником. Надаємо гарантію на всі роботи. Після здачі — підтримка та консультації протягом 12 місяців.',
  },
];

/**
 * How It Works — native <details> so «Детальніше» works without React hydration (prod proxy).
 *
 * @param {{ section?: import('@/types/cms').HowItWorksPageSection }} props
 */
export default function HowItWorks({ section = {} }) {
  const subTitle = section.sub_title || 'Процес роботи';
  const title = section.title || 'Як це працює';
  const intro =
    section.intro ||
    "Чітка та прозора система взаємодії — від першого дзвінка до здачі готового об'єкту";
  const steps = section.steps?.length ? section.steps : DEFAULT_STEPS;

  return (
    <section className="bg-card border-t border-border py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-px bg-gold" />
            <span className="text-gold text-xs font-semibold uppercase tracking-widest">{subTitle}</span>
            <div className="w-8 h-px bg-gold" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-foreground mb-4">{title}</h2>
          <div className="gold-divider" />
          {intro ? (
            <p className="text-muted-foreground mt-6 max-w-xl mx-auto text-sm leading-relaxed">{intro}</p>
          ) : null}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative" data-grv-hiw>
          <div className="hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent pointer-events-none" />

          {steps.map((step, idx) => {
            const Icon = resolveHowItWorksIcon(step.icon);
            const detail = typeof step.detail === 'string' ? step.detail.trim() : '';

            return (
              <details
                key={`${step.title}-${idx}`}
                data-grv-hiw-item
                className="grv-hiw-item relative text-center group rounded-xl border border-border bg-background/50 p-6 transition-all duration-300 open:border-gold/60 open:bg-background open:shadow-lg open:shadow-gold/10 hover:border-gold/40 hover:bg-background"
              >
                <summary className="grv-hiw-summary list-none cursor-pointer">
                  <div className="relative inline-flex mb-5">
                    <div className="grv-hiw-icon w-20 h-20 rounded-full border-2 border-border flex items-center justify-center transition-colors duration-300 group-hover:border-gold/60">
                      <Icon className="w-8 h-8 text-gold transition-colors duration-300" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 bg-gold-gradient rounded-full flex items-center justify-center text-dark text-xs font-black">
                      {idx + 1}
                    </span>
                  </div>

                  <h3 className="text-foreground font-bold text-base mb-2">{step.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>

                  {detail ? (
                    <div className="grv-hiw-more mt-3 text-gold text-xs font-semibold opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                      Детальніше ↓
                    </div>
                  ) : null}
                </summary>

                {detail ? (
                  <div className="grv-hiw-detail mt-4 pt-4 border-t border-gold/20 text-left">
                    <p className="text-sm text-foreground/80 leading-relaxed">{detail}</p>
                  </div>
                ) : null}
              </details>
            );
          })}
        </div>
      </div>
    </section>
  );
}
